/**
 * TNK-132 — User Management sub-page. Houses staff invitations and
 * role assignments (TNK-80). Used to live as an inline section on
 * /settings; folded into its own sub-page so the landing reads as a
 * tile grid. Owner-only at the route layer (the server already
 * enforces it on every member-mutating endpoint).
 */
import { useCallback, useEffect, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { Users } from "lucide-react";

import { BackButton } from "@/components/BackButton";
import {
  fetchConfig,
  fetchMembers,
  createInvite,
  inviteUrl,
  updateMemberRole,
  removeMember,
  type MemberRow,
  type StoreRole,
  type InviteRole,
} from "@/features/invites";
import { RequireRole } from "@/lib/auth";

export const Route = createFileRoute("/settings/user-management")({
  component: UserManagementPage,
});

const ROLE_OPTIONS: StoreRole[] = ["owner", "manager", "staff"];

function UserManagementPage() {
  return (
    <RequireRole min="owner">
      <section className="flex h-full flex-col">
        <div className="flex items-center justify-between gap-2 pb-3">
          <BackButton fallback={{ to: "/settings" }} label="Settings" />
        </div>

        <div className="border-b border-border pb-3">
          <div className="flex items-center gap-2">
            <Users className="h-5 w-5 text-muted-foreground" />
            <h1 className="text-2xl font-bold text-foreground">
              User management
            </h1>
          </div>
          <p className="text-sm text-muted-foreground">
            Invite staff to share the store and manage their roles. Owners run
            everything; managers run the store but can&apos;t connect eBay or
            manage members; staff add inventory and edit only their own cards.
          </p>
        </div>

        <div className="flex-1 space-y-4 overflow-y-auto py-4 pr-1">
          <MembersSection />
        </div>
      </section>
    </RequireRole>
  );
}

// Owner-only staff management (TNK-80). Returns null when MULTI_USER_ENABLED
// is off (the server gates it too — this just hides it for the solo
// operator).
function MembersSection() {
  const [enabled, setEnabled] = useState<boolean | null>(null);
  const [members, setMembers] = useState<MemberRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [inviteRole, setInviteRole] = useState<InviteRole>("staff");
  const [inviteLink, setInviteLink] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  // TNK-86 verify follow-up: role changes are pending until Save so a
  // bare select-onChange doesn't silently demote a manager when the
  // operator mis-clicks. Keyed by userId; absence = no pending change.
  const [pendingRoles, setPendingRoles] = useState<Record<string, StoreRole>>(
    {},
  );
  const [savingRoleFor, setSavingRoleFor] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    try {
      setMembers(await fetchMembers());
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not load members.");
    }
  }, []);

  useEffect(() => {
    void (async () => {
      const cfg = await fetchConfig();
      setEnabled(cfg.multiUserEnabled);
      if (cfg.multiUserEnabled) await refresh();
    })();
  }, [refresh]);

  if (enabled === null) {
    return <p className="text-sm text-muted-foreground">Loading…</p>;
  }
  if (!enabled) {
    return (
      <section className="space-y-2 rounded-md border border-border bg-card p-4">
        <h2 className="text-sm font-semibold text-foreground">
          Multi-user mode is off
        </h2>
        <p className="text-[11px] text-muted-foreground">
          The single-operator deployment doesn&apos;t need user management. Flip{" "}
          <code>MULTI_USER_ENABLED=true</code> in <code>.env</code> and restart
          the api to enable staff onboarding.
        </p>
      </section>
    );
  }

  const generate = async () => {
    setError(null);
    setCopied(false);
    try {
      setInviteLink(inviteUrl(await createInvite(inviteRole)));
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not create invite.");
    }
  };

  const copy = async () => {
    if (!inviteLink) return;
    try {
      await navigator.clipboard.writeText(inviteLink);
      setCopied(true);
    } catch {
      // Clipboard blocked — the link is shown for manual copy.
    }
  };

  const saveRole = async (userId: string) => {
    const role = pendingRoles[userId];
    if (!role) return;
    setError(null);
    setSavingRoleFor(userId);
    try {
      await updateMemberRole(userId, role);
      await refresh();
      setPendingRoles((prev) => {
        const next = { ...prev };
        delete next[userId];
        return next;
      });
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not change role.");
    } finally {
      setSavingRoleFor(null);
    }
  };

  const cancelRoleChange = (userId: string) => {
    setPendingRoles((prev) => {
      const next = { ...prev };
      delete next[userId];
      return next;
    });
  };

  const remove = async (userId: string) => {
    setError(null);
    try {
      await removeMember(userId);
      await refresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not remove member.");
    }
  };

  return (
    <section className="space-y-3 rounded-md border border-border bg-card p-4">
      {members && members.length > 0 && (
        <ul className="divide-y divide-border rounded-md border border-border">
          {members.map((m) => {
            const pending = pendingRoles[m.userId];
            const hasPending = pending !== undefined && pending !== m.role;
            const saving = savingRoleFor === m.userId;
            return (
              <li
                key={m.userId}
                className="flex items-center justify-between gap-3 p-2.5"
              >
                <div className="min-w-0">
                  <div className="truncate text-sm text-foreground">
                    {m.displayName}
                    {m.isSelf && (
                      <span className="ml-1 text-[11px] text-muted-foreground">
                        (you)
                      </span>
                    )}
                  </div>
                  <div className="text-[11px] text-muted-foreground">
                    {m.ebayLinked ? "eBay account" : "Passkey only"} · joined{" "}
                    {new Date(m.createdAt).toLocaleDateString()}
                  </div>
                </div>
                <div className="flex shrink-0 items-center gap-2">
                  <select
                    value={pending ?? m.role}
                    disabled={saving}
                    onChange={(e) =>
                      setPendingRoles((prev) => ({
                        ...prev,
                        [m.userId]: e.target.value as StoreRole,
                      }))
                    }
                    className={`rounded border bg-card px-2 py-1 text-[11px] text-foreground focus:border-primary focus:outline-none ${
                      hasPending ? "border-secondary" : "border-border"
                    }`}
                  >
                    {ROLE_OPTIONS.map((r) => (
                      <option key={r} value={r}>
                        {r}
                      </option>
                    ))}
                  </select>
                  {hasPending ? (
                    <>
                      <button
                        type="button"
                        onClick={() => void saveRole(m.userId)}
                        disabled={saving}
                        className="rounded bg-primary px-2 py-1 text-[11px] font-semibold text-primary-foreground hover:opacity-90 disabled:opacity-50"
                      >
                        {saving ? "Saving…" : "Save"}
                      </button>
                      <button
                        type="button"
                        onClick={() => cancelRoleChange(m.userId)}
                        disabled={saving}
                        className="rounded border border-border px-2 py-1 text-[11px] text-muted-foreground hover:bg-muted disabled:opacity-50"
                      >
                        Cancel
                      </button>
                    </>
                  ) : (
                    <button
                      type="button"
                      onClick={() => void remove(m.userId)}
                      className="rounded border border-border px-2 py-1 text-[11px] text-muted-foreground hover:bg-muted hover:text-destructive"
                    >
                      Remove
                    </button>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}

      <div className="space-y-2 rounded-md border border-dashed border-border p-3">
        <div className="flex items-end gap-2">
          <label className="space-y-1">
            <span className="text-[11px] font-medium text-muted-foreground">
              Invite a
            </span>
            <select
              value={inviteRole}
              onChange={(e) => setInviteRole(e.target.value as InviteRole)}
              className="block rounded-md border border-border bg-card px-3 py-2 text-sm text-foreground focus:border-primary focus:outline-none"
            >
              <option value="staff">Staff</option>
              <option value="manager">Manager</option>
            </select>
          </label>
          <button
            type="button"
            onClick={() => void generate()}
            className="shrink-0 rounded-md bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground hover:opacity-90"
          >
            Create invite link
          </button>
        </div>

        {inviteLink && (
          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <input
                readOnly
                value={inviteLink}
                onFocus={(e) => e.currentTarget.select()}
                className="min-w-0 flex-1 rounded-md border border-border bg-muted px-2 py-1.5 text-[11px] text-foreground"
              />
              <button
                type="button"
                onClick={() => void copy()}
                className="shrink-0 rounded border border-border px-2 py-1.5 text-[11px] text-foreground hover:bg-muted"
              >
                {copied ? "Copied" : "Copy"}
              </button>
            </div>
            <p className="text-[11px] text-muted-foreground">
              Share this link with the new {inviteRole}. It expires in 7 days.
            </p>
          </div>
        )}
      </div>

      {error && <p className="text-[11px] text-destructive">{error}</p>}
    </section>
  );
}
